/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./BasePropertyAction',
	'jquery',
	'underscore',
	'../../../../lib/@waca/dashboard-common/dist/ui/interaction/Utils',
	'../../../../app/nls/StringResources'
], function(BaseClass, $, _, utils, stringResources) {

	var Align = null;
	Align = BaseClass.extend({
		init: function(controller) {
			this.controller = controller;
			Align.inherited('init', this, arguments);
			this.selectedNodes = [];
		},

		/**
		 * Called by the interaction manager when there is a new selection
		 *
		 * @param nodes
		 */
		newSelection: function(nodes) {
			this.selectedNodes = nodes;
		},

		getProperties: function() {
			const icons = this.dashboardApi.getFeature('Icons');
			const isReadOnly = this.selectedNodes.length <= 1;
			return [{
				'type': 'IconPicker',
				'label': stringResources.get('toolbarActionAlign'),
				'name': 'alignWidget',
				'id': 'alignWidget',
				'tabName': stringResources.get('tabName_general'),
				'sectionName': stringResources.get('sectionName_layout'),
				'sectionOpened': true,
				'readOnly': isReadOnly,
				'items': [{
					'name': 'left',
					'label': stringResources.get('alignWidgetLeft'),
					'value': icons.getIcon('align-left').id
				}, {
					'name': 'center',
					'label': stringResources.get('alignWidgetCenter'),
					'value': icons.getIcon('align-horizontally').id
				}, {
					'name': 'right',
					'label': stringResources.get('alignWidgetRight'),
					'value': icons.getIcon('align-right').id
				}, {
					'name': 'top',
					'label': stringResources.get('alignWidgetTop'),
					'value': icons.getIcon('align-top').id
				}, {
					'name': 'middle',
					'label': stringResources.get('alignWidgetMiddle'),
					'value': icons.getIcon('align-vertically').id
				}, {
					'name': 'bottom',
					'label': stringResources.get('alignWidgetBottom'),
					'value': icons.getIcon('align-bottom').id
				}],
				onChange: this.onAlignIconClick.bind(this),
				order: 10
			}];
		},

		onAlignIconClick: function(propertyId, selection) {
			if (selection && selection.length && selection[0].name && this.selectedNodes.length > 1) {
				const position = selection[0].name;
				this.applyAlignment(position);
			}
		},

		getWidgetBoxes: function(node) {
			const widgetPos = utils.position(node);
			const widgetSize = utils.widgetSize(node);
			let result = {
				boundingBox: $(node).position(),
				widgetBox: {
					top: widgetPos.top,
					left: widgetPos.left,
					width: widgetSize.width,
					height: widgetSize.height
				}
			};
			const boundingRect = node.getBoundingClientRect();
			result.boundingBox.width = boundingRect.width;
			result.boundingBox.height = boundingRect.height;
			return result;
		},

		applyAlignment: function(position) {
			const alignmentFns = {
				left: this.getLeftAlignmentStyle,
				center: this.getCenterAlignmentStyle,
				right: this.getRightAlignmentStyle,
				top: this.getTopAlignmentStyle,
				middle: this.getMiddleAlignmentStyle,
				bottom: this.getBottomAlignmentStyle
			};
			let widgetBoxes = [];
			const widgetBox = this.getWidgetBoxes(this.selectedNodes[0]);
			widgetBoxes.push(widgetBox);
			let bounds = {
				top: widgetBox.boundingBox.top,
				left: widgetBox.boundingBox.left,
				bottom: widgetBox.boundingBox.top + widgetBox.boundingBox.height,
				right: widgetBox.boundingBox.left + widgetBox.boundingBox.width
			};
			for (let i=1; i<this.selectedNodes.length; i++) {
				const widgetBox = this.getWidgetBoxes(this.selectedNodes[i]);
				widgetBoxes.push(widgetBox);
				bounds.top = Math.min(bounds.top, widgetBox.boundingBox.top);
				bounds.left = Math.min(bounds.left, widgetBox.boundingBox.left);
				bounds.bottom = Math.max(bounds.bottom, widgetBox.boundingBox.top + widgetBox.boundingBox.height);
				bounds.right = Math.max(bounds.right, widgetBox.boundingBox.left + widgetBox.boundingBox.width);
			}
			let modelInfoArray = [];
			for (let i=0; i<this.selectedNodes.length; i++) {
				const updatedStyle = alignmentFns[position](bounds, widgetBoxes[i], this.selectedNodes[i]);
				if (updatedStyle) {
					modelInfoArray.push({
						style: updatedStyle,
						id: this.selectedNodes[i]._layout.model.id
					});
				}
			}
			if (modelInfoArray.length) {
				this.updateModel(modelInfoArray);
				this.refreshProperties();
			}
		},

		getLeftAlignmentStyle: function(bounds, widgetBox, node) {
			let leftPos = bounds.left;
			if (Math.round(widgetBox.boundingBox.left) !== Math.round(widgetBox.widgetBox.left)) {
				const offset = widgetBox.boundingBox.left - widgetBox.widgetBox.left;
				leftPos = leftPos - offset;
			}
			//Sanity check the position against the minimum
			leftPos = Math.max(leftPos, node._layout.parentLayout.getMinimumLeft());
			return {
				left: leftPos + 'px'
			};
		},

		getCenterAlignmentStyle: function(bounds, widgetBox, node) {
			const centerLine = bounds.left + ((bounds.right - bounds.left)/2);
			let leftPos = centerLine - (widgetBox.widgetBox.width/2);
			if (Math.round(widgetBox.boundingBox.left) !== Math.round(widgetBox.widgetBox.left)) {
				const offset = (widgetBox.boundingBox.left + (widgetBox.boundingBox.width/2)) - (widgetBox.widgetBox.left + (widgetBox.widgetBox.width/2));
				leftPos = leftPos - offset;
			}
			//Sanity check the position against the minimum
			leftPos = Math.max(leftPos, node._layout.parentLayout.getMinimumLeft());
			return {
				left: leftPos + 'px'
			};
		},

		getRightAlignmentStyle: function(bounds, widgetBox, node) {
			let leftPos = bounds.right - widgetBox.widgetBox.width;
			if (Math.round(widgetBox.boundingBox.width) !== Math.round(widgetBox.widgetBox.width)) {
				const offset = (widgetBox.boundingBox.left + widgetBox.boundingBox.width) - (widgetBox.widgetBox.left + widgetBox.widgetBox.width);
				leftPos = leftPos - offset;
			}
			//Sanity check the position against the minimum
			leftPos = Math.max(leftPos, node._layout.parentLayout.getMinimumLeft());
			return {
				left: leftPos + 'px'
			};
		},

		getTopAlignmentStyle: function(bounds, widgetBox, node) {
			let topPos = bounds.top;
			if (Math.round(widgetBox.boundingBox.top) !== Math.round(widgetBox.widgetBox.top)) {
				const offset = widgetBox.boundingBox.top - widgetBox.widgetBox.top;
				topPos = topPos - offset;
			}
			//Sanity check the position against the minimum
			topPos = Math.max(topPos, node._layout.parentLayout.getMinimumTop());
			return {
				top: topPos + 'px'
			};
		},

		getMiddleAlignmentStyle: function(bounds, widgetBox, node) {
			const middleLine = bounds.top + ((bounds.bottom - bounds.top)/2);
			let topPos = middleLine - (widgetBox.widgetBox.height/2);
			if (Math.round(widgetBox.boundingBox.top) !== Math.round(widgetBox.widgetBox.top)) {
				const offset = (widgetBox.boundingBox.top + (widgetBox.boundingBox.height/2)) - (widgetBox.widgetBox.top + (widgetBox.widgetBox.height/2));
				topPos = topPos - offset;
			}
			//Sanity check the position against the minimum
			topPos = Math.max(topPos, node._layout.parentLayout.getMinimumTop());
			return {
				top: topPos + 'px'
			};
		},

		getBottomAlignmentStyle: function(bounds, widgetBox, node) {
			let topPos = bounds.bottom - widgetBox.widgetBox.height;
			if (Math.round(widgetBox.boundingBox.height) !== Math.round(widgetBox.widgetBox.height)) {
				const offset = (widgetBox.boundingBox.top + widgetBox.boundingBox.height) - (widgetBox.widgetBox.top + widgetBox.widgetBox.height);
				topPos = topPos - offset;
			}
			//Sanity check the position against the minimum
			topPos = Math.max(topPos, node._layout.parentLayout.getMinimumTop());
			return {
				top: topPos + 'px'
			};
		}
	});

	return Align;

});
